import React from 'react';
import CodeBlock from './CodeBlock';
import { cssPartSnippet } from './utils';

export default function HelpContent() {
  return (
    <div className="mx-auto max-w-3xl">
      <section id="overview" className="mb-8 scroll-mt-56">
        <h2 className="text-xl font-semibold">Overview</h2>
        <p className="text-slate-300">
          Pintar Studios web tools is drop-in web components you can embed on any website. It uses{' '}
          <code>Shadow DOM</code> to isolate its styles from your page.
        </p>
        <ul className="mt-3 list-disc space-y-1 pl-6 text-slate-300">
          <li>Embeddable via a single script and a custom element.</li>
          <li>
            Views: <code>Karta</code>, <code>Solstudie</code>, <code>Panorama</code>, <code>Tabell</code>, or default
            “Bostadsväljaren”.
          </li>
          {/* <li>Non-intrusive: your site’s CSS won’t leak in, thanks to Shadow DOM.</li> */}
        </ul>
      </section>

      <section id="views" className="mb-8 scroll-mt-32">
        <h2 className="text-xl font-semibold">Views</h2>
        <p className="text-slate-300">
          Control the initial view with the <code>view</code> attribute on <code>&lt;pintar-studios-webtools&gt;</code>.
          If omitted or empty, the housing selector is shown.
        </p>
        <div className="overflow-x-auto rounded-xl border border-slate-800">
          <table className="w-full min-w-[600px] text-left">
            <thead className="border-b border-slate-800 bg-[#0f1116]">
              <tr>
                <th className="px-3 py-2">View</th>
                <th className="px-3 py-2">Value</th>
                <th className="px-3 py-2">What it shows</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              <tr>
                <td className="px-3 py-2">Default (selector)</td>
                <td className="px-3 py-2">
                  <code>(empty)</code>
                </td>
                <td className="px-3 py-2">Interactive housing selector.</td>
              </tr>
              <tr>
                <td className="px-3 py-2">Karta</td>
                <td className="px-3 py-2">
                  <code>map</code>
                </td>
                <td className="px-3 py-2">Showing only the map view of the project site. </td>
              </tr>
              <tr>
                <td className="px-3 py-2">Solstudie</td>
                <td className="px-3 py-2">
                  <code>solar</code>
                </td>
                <td className="px-3 py-2">Showing only the solar study of the project site.</td>
              </tr>
              <tr>
                <td className="px-3 py-2">Panorama</td>
                <td className="px-3 py-2">
                  <code>panorama</code>
                </td>
                <td className="px-3 py-2">Showing only the panorama view.</td>
              </tr>
              <tr>
                <td className="px-3 py-2">Table</td>
                <td className="px-3 py-2">
                  <code>table</code>
                </td>
                <td className="px-3 py-2">Showing only the table view of Bostadsväljaren.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section id="parameters" className="mb-8 scroll-mt-32">
        <h2 className="text-xl font-semibold">Parameters</h2>
        <p className="text-slate-300">Add them as attributes on the custom element.</p>

        <div className="overflow-x-auto rounded-xl border border-slate-800">
          <table className="w-full min-w-[600px] text-left">
            <thead className="border-b border-slate-800 bg-[#0f1116]">
              <tr>
                <th className="px-3 py-2">Attribute</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Applies to</th>
                <th className="px-3 py-2">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              <tr>
                <td className="px-3 py-2">
                  <code>company</code>
                </td>
                <td className="px-3 py-2">
                  <code>string</code>
                </td>
                <td className="px-3 py-2">-</td>
                <td className="px-3 py-2">Company identifier</td>
              </tr>
              <tr>
                <td className="px-3 py-2">
                  <code>sitename</code>
                </td>
                <td className="px-3 py-2">
                  <code>string</code>
                </td>
                <td className="px-3 py-2">-</td>
                <td className="px-3 py-2">Project/site identifier</td>
              </tr>
              <tr>
                <td className="px-3 py-2">
                  <code>view</code>
                </td>
                <td className="px-3 py-2">
                  <code>{`"" | "map" | "solar" | "panorama" | "table"`}</code>
                </td>
                <td className="px-3 py-2">-</td>
                <td className="px-3 py-2">View identifier. Empty shows Bostadsväljaren.</td>
              </tr>
              <tr>
                <td className="px-3 py-2">
                  <code>max-width</code>
                </td>
                <td className="px-3 py-2">
                  <code>string</code>
                </td>
                <td className="px-3 py-2">Bostadsväljaren, Karta, Panorama, Solstudie</td>
                <td className="px-3 py-2">
                  Maximum rendered width of the widget container. Default: 64rem. Limited to: 90rem
                </td>
              </tr>
              <tr>
                <td className="px-3 py-2">
                  <code>height</code>
                </td>
                <td className="px-3 py-2">
                  <code>string</code>
                </td>
                <td className="px-3 py-2">Panorama</td>
                <td className="px-3 py-2">Explicit height for the panorama view container. Default: 600px.</td>
              </tr>
              <tr>
                <td className="px-3 py-2">
                  <code>max-table-height</code>
                </td>
                <td className="px-3 py-2">
                  <code>string</code>
                </td>
                <td className="px-3 py-2">Bostadsväljaren, Table</td>
                <td className="px-3 py-2">Max height for the apartments table. Default: 100%</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="mt-3 text-slate-400">
          <strong>Tip:</strong> Parameter values are strings. Use CSS lengths as needed (e.g., <code>100%</code>,{' '}
          <code>960px</code>).
        </p>
      </section>

      <section id="styling" className="mb-8 scroll-mt-32">
        <h2 className="text-xl font-semibold">
          Styling via <code>::part</code>
        </h2>
        <p className="text-slate-300">
          The widget ships styles inside Shadow DOM, so your site’s CSS won’t affect it. If you want light-touch
          overrides, target these exposed parts from your page:
        </p>
        <ul className="mt-3 list-disc space-y-1 pl-6 text-slate-300">
          <li>
            <code>::part(wrapper)</code> – Wrapper div element, add background, color, font-size etc.
          </li>
          <li>
            <code>::part(view-border)</code> – Border around the floorplan after clicking on a residence, set background
            color matching your site.
          </li>
          <li>
            <code>::part(apartment-button)</code> – Buttons on the floorplan after clicking on a residence, set button
            style matching your site.
          </li>
        </ul>
        <p className="mt-3 text-slate-400">
          Example:
          <CodeBlock code={cssPartSnippet()} language="css" ariaLabel="Embed snippet" />
        </p>
      </section>

      {/* <section id="support" className="mb-8 scroll-mt-32">
        <h2 className="text-xl font-semibold">Notes</h2>
        <ul className="mt-3 list-disc space-y-1 pl-6 text-slate-300">
          <li>
            Place the <code>&lt;script async&gt;</code> before the custom element.
          </li>
          <li>
            The custom element tag is <code>&lt;pintar-studios-webtools&gt;</code>.
          </li>
          <li>
            When <code>view</code> is empty or omitted, the housing selector is displayed.
          </li>
        </ul>
      </section> */}
    </div>
  );
}
